import { useInView } from "@/hooks/useInView";
import { ClipboardList, Search, FileText, Banknote, Check } from "lucide-react";

const steps = [
  {
    icon: ClipboardList,
    number: "01",
    title: "Wypełnij formularz",
    desc: "Podaj imię, telefon i lokalizację swojej działki. To zajmuje 30 sekund i jest całkowicie bezpłatne.",
    color: "emerald",
  },
  {
    icon: Search,
    number: "02",
    title: "Bezpłatny audyt lokalizacji",
    desc: "Nasi specjaliści oceniają potencjał Twojej nieruchomości i przygotowują indywidualną wycenę, którą przedstawiamy podczas rozmowy.",
    color: "emerald",
  },
  {
    icon: FileText,
    number: "03",
    title: "My zajmujemy się formalnościami",
    desc: "Cała papierologia leży po naszej stronie — Ty podpisujesz tylko umowę dzierżawy.",
    color: "emerald",
  },
  {
    icon: Banknote,
    number: "04",
    title: "Czynsz regularnie na konto",
    desc: "Pierwszy czynsz wpływa od roku, w którym rozpoczynamy prace budowlane (płatność z góry, do 28 lutego). Potem corocznie — z waloryzacją GUS, przez cały okres umowy.",
    color: "amber",
  },
];

const formalities = [
  "Decyzja o warunkach zabudowy (WZ)",
  "Decyzja środowiskowa",
  "Pozwolenie na budowę",
  "Operat geodezyjny i mapa do celów projektowych",
  "Projekt budowlany i konstrukcyjny",
  "Pomiary EMF i raport zgodności z UKE",
  "Uzgodnienia z operatorem energetycznym (przyłącze)",
  "Negocjacje umowy z operatorem telekomunikacyjnym",
  "Pełne ubezpieczenie OC inwestycji",
];

export default function HowItWorks() {
  const [ref, inView] = useInView();

  return (
    <section id="jak-to-dziala" className="py-24 bg-white" data-testid="how-it-works-section">
      <div className="max-w-7xl mx-auto px-6">
        <div
          ref={ref}
          className={`text-center mb-16 transition-all duration-700 ${
            inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        >
          <span className="text-emerald-700 text-sm font-semibold uppercase tracking-widest">
            Prosty proces
          </span>
          <h2 className="text-4xl sm:text-5xl font-black text-slate-900 mt-2 tracking-tight">
            Jak to działa?
          </h2>
          <p className="text-slate-600 text-lg mt-4 max-w-xl mx-auto">
            Od pierwszego kontaktu do regularnych wypłat — cztery kroki, przez
            które przeprowadzimy Cię za rękę.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {steps.map((step, i) => (
            <StepCard key={step.number} step={step} index={i} />
          ))}
        </div>

        <FormalitiesPanel parentInView={inView} />
      </div>
    </section>
  );
}

function StepCard({ step, index }) {
  const [ref, inView] = useInView();
  const Icon = step.icon;

  return (
    <div
      ref={ref}
      className={`relative transition-all duration-700 ${
        inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
      }`}
      style={{ transitionDelay: `${index * 100}ms` }}
      data-testid={`step-card-${index + 1}`}
    >
      <div className="bg-slate-50 rounded-2xl p-6 border border-slate-200 h-full hover:shadow-lg hover:shadow-emerald-900/5 hover:-translate-y-1 transition-all duration-300">
        <div className="flex items-center justify-between mb-5">
          <div
            className={`w-12 h-12 rounded-xl flex items-center justify-center ${
              step.color === "amber" ? "bg-amber-100" : "bg-emerald-100"
            }`}
          >
            <Icon
              className={`w-6 h-6 ${
                step.color === "amber" ? "text-amber-700" : "text-emerald-700"
              }`}
              aria-hidden="true"
            />
          </div>
          <span
            className={`text-4xl font-black font-display ${
              step.color === "amber" ? "text-amber-200" : "text-emerald-200"
            }`}
          >
            {step.number}
          </span>
        </div>
        <h3 className="font-bold text-slate-900 text-lg mb-2">
          {step.title}
        </h3>
        <p className="text-slate-600 text-sm leading-relaxed">{step.desc}</p>
      </div>
    </div>
  );
}

function FormalitiesPanel({ parentInView }) {
  const [ref, inView] = useInView(0.1);

  return (
    <div
      ref={ref}
      className={`relative bg-gradient-to-br from-slate-900 to-emerald-950 rounded-3xl p-8 sm:p-10 transition-all duration-700 ${
        inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
      }`}
      style={{ transitionDelay: parentInView ? "200ms" : "0ms" }}
      data-testid="formalities-panel"
    >
      <div className="grid lg:grid-cols-[1fr_1.4fr] gap-8 items-start">
        <div>
          <span className="inline-flex items-center gap-2 bg-emerald-500/15 border border-emerald-400/40 text-emerald-200 text-xs font-bold uppercase tracking-widest px-3 py-1.5 rounded-full mb-5">
            Krok 03 w szczegółach
          </span>
          <h3 className="text-2xl sm:text-3xl font-black text-white tracking-tight mb-4 leading-tight">
            Konkretnie czym się zajmujemy
            <br />
            <span className="text-emerald-400">w Twoim imieniu</span>
          </h3>
          <p className="text-slate-300 text-sm leading-relaxed">
            Każdy etap administracyjny prowadzimy od początku do końca. Poniżej lista typowych dokumentów,
            decyzji i postępowań, w których nie musisz brać udziału.
          </p>
        </div>

        <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-3">
          {formalities.map((f, i) => (
            <li
              key={f}
              className={`flex items-start gap-3 text-slate-100 text-sm transition-all duration-500 ${
                inView ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-4"
              }`}
              style={{ transitionDelay: `${400 + i * 60}ms` }}
            >
              <div className="w-5 h-5 bg-emerald-500/20 border border-emerald-400/40 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <Check className="w-3 h-3 text-emerald-300" aria-hidden="true" />
              </div>
              <span className="leading-relaxed">{f}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
